import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Import September and October data directly
import septemberData from '../data/September_2025.json';
import octoberData from '../data/October_2025.json';

export interface LocalProduct {
  name: string;
  quantity: number;
  price: number;
  total: number;
}

export interface LocalOrder {
  index: number;
  orderName: string;
  amount: number;
  dateTimeText: string;
  status: string;
  products: LocalProduct[];
  orderNumber: string;
  hasDetailedData: boolean;
  pageNumber?: number;
  date: string;
  scrapedAt?: string;
  paymentMethod?: 'card' | 'cash';
  tip?: number;
}

export interface MonthlyOrderData {
  month: number;
  year: number;
  orders: LocalOrder[];
  lastUpdated: string;
  dateRange: {
    start: string;
    end: string;
  };
  totalOrders: number;
}

class LocalOrderDataService {
  private orderData: Map<string, MonthlyOrderData> = new Map();

  constructor() {
    // Load September and October data on initialization
    this.loadMonthData('September_2025', septemberData as MonthlyOrderData);
    this.loadMonthData('October_2025', octoberData as MonthlyOrderData);
  }

  private loadMonthData(key: string, data: MonthlyOrderData) {
    this.orderData.set(key, data);
  }

  /**
   * Get all available months of data
   */
  getAvailableMonths(): string[] {
    return Array.from(this.orderData.keys());
  }

  /**
   * Get orders for a specific month
   */
  getMonthlyOrders(month: number, year: number): LocalOrder[] {
    const key = `${this.getMonthName(month)}_${year}`;
    const data = this.orderData.get(key);
    return data ? data.orders : [];
  }

  /**
   * Get all orders across all months
   */
  getAllOrders(): LocalOrder[] {
    const allOrders: LocalOrder[] = [];
    this.orderData.forEach(monthData => {
      allOrders.push(...monthData.orders.map(order => ({
        ...order,
        amount: typeof order.amount === 'string' ? parseFloat(order.amount) : order.amount
      })));
    });
    return allOrders.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  /**
   * Get orders within a date range
   */
  getOrdersByDateRange(startDate: Date, endDate: Date): LocalOrder[] {
    const allOrders = this.getAllOrders();
    return allOrders.filter(order => {
      const orderDate = new Date(order.date);
      return orderDate >= startDate && orderDate <= endDate;
    });
  }

  /**
   * Get orders by payment method
   */
  getOrdersByPaymentMethod(method: 'card' | 'cash' | 'all'): LocalOrder[] {
    if (method === 'all') {
      return this.getAllOrders();
    }
    return this.getAllOrders().filter(order => order.paymentMethod === method);
  }

  /**
   * Search orders by reference/name
   */
  searchOrders(query: string): LocalOrder[] {
    const lowerQuery = query.toLowerCase();
    return this.getAllOrders().filter(order => 
      order.orderName?.toLowerCase().includes(lowerQuery) ||
      order.orderNumber?.toLowerCase().includes(lowerQuery) ||
      order.dateTimeText?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Get summary statistics
   */
  getSummaryStats(orders: LocalOrder[]) {
    const totalOrders = orders.length;
    const totalAmount = orders.reduce((sum, order) => sum + order.amount, 0);
    const averageTicket = totalOrders > 0 ? totalAmount / totalOrders : 0;
    
    // Group by payment method
    const paymentMethods = orders.reduce((acc, order) => {
      const method = order.paymentMethod || 'card';
      if (!acc[method]) {
        acc[method] = { count: 0, total: 0 };
      }
      acc[method].count++;
      acc[method].total += order.amount;
      return acc;
    }, {} as Record<string, { count: number; total: number }>);

    // Group by date
    const dailyBreakdown = orders.reduce((acc, order) => {
      try {
        const date = format(new Date(order.date), 'yyyy-MM-dd');
        if (!acc[date]) {
          acc[date] = { count: 0, total: 0, orders: [] };
        }
        acc[date].count++;
        acc[date].total += order.amount;
        acc[date].orders.push(order);
        return acc;
      } catch {
        return acc;
      }
    }, {} as Record<string, { count: number; total: number; orders: LocalOrder[] }>);

    // Calculate products breakdown
    const productsBreakdown = orders.reduce((acc, order) => {
      if (order.hasDetailedData && order.products) {
        order.products.forEach(product => {
          if (!acc[product.name]) {
            acc[product.name] = { 
              quantity: 0, 
              revenue: 0, 
              avgPrice: 0,
              orders: 0
            };
          }
          acc[product.name].quantity += product.quantity;
          acc[product.name].revenue += product.total;
          acc[product.name].orders++;
          acc[product.name].avgPrice = product.price;
        });
      }
      return acc;
    }, {} as Record<string, { quantity: number; revenue: number; avgPrice: number; orders: number }>);

    const totalTips = orders.reduce((sum, order) => sum + (order.tip || 0), 0);
    const ordersWithTips = orders.filter(o => o.tip && o.tip > 0).length;

    return {
      totalOrders,
      totalAmount,
      averageTicket,
      totalTips,
      ordersWithTips,
      paymentMethods,
      dailyBreakdown,
      productsBreakdown,
      ordersWithProducts: orders.filter(o => o.hasDetailedData).length,
      dateRange: {
        start: orders.length > 0 ? orders[orders.length - 1].date : null,
        end: orders.length > 0 ? orders[0].date : null
      }
    };
  }

  /**
   * Get top selling products
   */
  getTopProducts(limit: number = 10): Array<{ name: string; quantity: number; revenue: number }> {
    const allOrders = this.getAllOrders();
    const productMap = new Map<string, { quantity: number; revenue: number }>();

    allOrders.forEach(order => {
      if (order.hasDetailedData && order.products) {
        order.products.forEach(product => {
          const existing = productMap.get(product.name) || { quantity: 0, revenue: 0 };
          existing.quantity += product.quantity;
          existing.revenue += product.total;
          productMap.set(product.name, existing);
        });
      }
    });

    return Array.from(productMap.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, limit);
  }

  private getMonthName(month: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  }

  /**
   * Format order for display
   */
  formatOrderForDisplay(order: LocalOrder) {
    try {
      let cashier = 'Unknown';
      let formattedDate = '';
      let formattedTime = '';
      let displayName = order.orderName || `#${order.orderNumber}`;
      
      if (order.paymentMethod === 'cash' && order.dateTimeText?.includes('cash_transaction')) {
        const dateTimeMatch = order.dateTimeText.match(/(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2}:\d{2})/);
        if (dateTimeMatch) {
          const [, dateStr, timeStr] = dateTimeMatch;
          const [day, month, year] = dateStr.split('/');
          const orderDate = new Date(`${year}-${month}-${day}T${timeStr}`);
          formattedDate = format(orderDate, 'dd/MM/yyyy', { locale: es });
          formattedTime = format(orderDate, 'HH:mm', { locale: es });
        }
        cashier = 'Efectivo';
        const amountMatch = order.orderName?.match(/Efectivo-(.+)/);
        displayName = amountMatch ? `Efectivo $${amountMatch[1]}` : order.orderName;
      } else {
        const cashierMatch = order.dateTimeText?.match(/^([^-]+)\s*-/);
        cashier = cashierMatch ? cashierMatch[1].trim() : 'Unknown';
        
        const orderDate = new Date(order.date);
        formattedDate = format(orderDate, 'dd/MM/yyyy', { locale: es });
        formattedTime = format(orderDate, 'HH:mm', { locale: es });
      }

      return {
        ...order,
        cashier,
        formattedDate,
        formattedTime,
        displayName,
        paymentMethodDisplay: order.paymentMethod === 'cash' ? 'Efectivo' : 'Tarjeta'
      };
    } catch {
      return {
        ...order,
        cashier: 'Unknown',
        formattedDate: '',
        formattedTime: '',
        displayName: order.orderName || `#${order.orderNumber}`,
        paymentMethodDisplay: order.paymentMethod === 'cash' ? 'Efectivo' : 'Tarjeta'
      };
    }
  }
}

export const localOrderDataService = new LocalOrderDataService();